import { APIGatewayResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { AxiosError } from 'axios';
import { useContext, useContextSelector } from 'use-context-selector';
import { CheckMandateResponseBody } from '../../../../authorizations/src/domain/Authorization/entities/CheckMandateResponseDTO';
import { ConsolidatedTransactionDetails } from '../../domain/Transaction/entities/ConsolidatedTransaction';
import { ConsolidatedTransactionDetailsPrint } from '../../domain/Transaction/entities/ConsolidatedTransaction/ConsolidatedTransactionDetailsPrintResponseDTO';
import { ConsolidatedTransactionDetailsContext } from './ConsolidatedTransactionDetailsContext';

const useConsolidatedTransactionDetailsState = () => {
  const context = useContext(ConsolidatedTransactionDetailsContext);

  if (context === undefined) {
    throw new Error(
      'useConsolidatedTransactionDetailsState must be used within ConsolidatedTransactionDetailsProvider',
    );
  }

  return context;
};

const useConsolidatedTransactionDetailsData = () => {
  const context = useContextSelector(
    ConsolidatedTransactionDetailsContext,
    state => state.consolidatedTransactionDetails ?? null,
  );

  return context;
};

const useGetTransactionDocuments = () => {
  const context = useContextSelector(ConsolidatedTransactionDetailsContext, state => state.getTransactionDocuments);

  if (context === undefined) {
    throw new Error('useGetTransactionDocuments must be used within ConsolidatedTransactionDetailsProvider');
  }

  return context;
};

const useGetSingleFileDocumentData = () => {
  const context = useContextSelector(ConsolidatedTransactionDetailsContext, state => state.getSingleFileDocumentData);

  if (context === undefined) {
    throw new Error('useGetTransactionSingleDocumentData must be used within ConsolidatedTransactionDetailsProvider');
  }

  return context;
};

const useOnSelectConsolidatedTransaction = () => {
  const context = useContextSelector(
    ConsolidatedTransactionDetailsContext,
    state => state.onSelectConsolidatedTransaction,
  );

  if (context === undefined) {
    throw new Error('useOnSelectConsolidatedTransaction must be used within ConsolidatedTransactionDetailsProvider');
  }

  return context;
};

const useResetSelectedConsolidatedTransaction = () => {
  const context = useContextSelector(
    ConsolidatedTransactionDetailsContext,
    state => state.resetSelectedConsolidatedTransaction,
  );

  if (context === undefined) {
    throw new Error('useConsolidatedTransactionListFilters must be used within ConsolidatedTransactionDetailsProvider');
  }

  return context;
};

const useSelectedConsolidatedTransaction = () => {
  const context = useContextSelector(
    ConsolidatedTransactionDetailsContext,
    state => state.selectedConsolidatedTransaction,
  );

  if (context === undefined) {
    throw new Error('useConsolidatedTransactionListFilters must be used within ConsolidatedTransactionDetailsProvider');
  }

  return context;
};

const useConsolidatedTransactionDetailsProviderLoadingState = (
  loadingProperty: 'isLoadingConsolidatedTransactionDetails' | 'isLoadingCheckAuthorization',
) => {
  const context = useContextSelector(
    ConsolidatedTransactionDetailsContext,
    state => state.loadingState[loadingProperty],
  );

  if (context === undefined) {
    throw new Error(
      'useConsolidatedTransactionsProviderLoadingState must be used within ConsolidatedTransactionDetailsProvider',
    );
  }

  return context;
};

const useCheckAuthorization = () =>
  useContextSelector(ConsolidatedTransactionDetailsContext, state => state.checkAuthorizationData);

const useOnCheckAuthorizationMutation = () => {
  const context = useContextSelector(
    ConsolidatedTransactionDetailsContext,
    state => state.onCheckAuthorizationMutation,
  );

  if (context === undefined) {
    throw new Error('useOnCheckAuthorizationMutation must be used within ConsolidatedTransactionDetailsProvider');
  }

  return context;
};

const useConsolidatedTransactionDetailsProviderErrorState = (
  errorProperty: 'consolidatedTransactionDetailsError' | 'checkAuthorizationError',
): AxiosError<
  APIGatewayResponse<ConsolidatedTransactionDetails | ConsolidatedTransactionDetailsPrint | CheckMandateResponseBody>,
  any
> | null => {
  const context = useContextSelector(
    ConsolidatedTransactionDetailsContext,
    state => state.errorState[errorProperty] ?? null,
  );

  // if (context === undefined) {
  //   throw new Error(
  //     'useConsolidatedTransactionsProviderErrorState must be used within ConsolidatedTransactionsProvider',
  //   );
  // }

  return context;
};

const useRefetchConsolidatedTransactionDetails = () => {
  const context = useContextSelector(
    ConsolidatedTransactionDetailsContext,
    state => state.refetchConsolidatedTransactionDetails,
  );

  if (context === undefined) {
    throw new Error(
      'useRefetchConsolidatedTransactionDetails must be used within ConsolidatedTransactionDetailsProvider',
    );
  }

  return context;
};

export {
  useConsolidatedTransactionDetailsState,
  useConsolidatedTransactionDetailsData,
  useConsolidatedTransactionDetailsProviderLoadingState,
  useConsolidatedTransactionDetailsProviderErrorState,
  useGetTransactionDocuments,
  useGetSingleFileDocumentData,
  useOnSelectConsolidatedTransaction,
  useSelectedConsolidatedTransaction,
  useResetSelectedConsolidatedTransaction,
  useRefetchConsolidatedTransactionDetails,
  useCheckAuthorization,
  useOnCheckAuthorizationMutation,
};
